import dayjs, { Dayjs } from "dayjs";
import { useField, useFormikContext } from "formik";
import { forwardRef, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import useWindowSize from "../../../../customHooks/useWindowSize";
import ImageImports from "../../../../utils/ImageImports";
import Button from "../../../Button/Button";
import MiniTooltip from "../../MiniTooltip/MiniTooltip";
import classes from "./DatePickerField.module.css";

const { tooltip, calendar } = ImageImports;
const DatePickerFields = ({ ...props }: DatePickerFieldInterface) => {
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField(props);
  const inputref = useRef(null);

  return (
    <div className={`${classes.form_field_row} mb-0`}>
      <label htmlFor={props.name} className={classes.input_label}>
        {props.labelName}
        {props.tooltip?.text.length && (
          <div className={classes.cursor_pointer}>
            <MiniTooltip text={props.tooltip.text} space={props.tooltip.space} placement={props.tooltip.placement}>
              <img src={tooltip} className={classes.tooltip_icon} alt="tool tip" />
            </MiniTooltip>
          </div>
        )}
      </label>
      <div className={`${classes.form_group}`}>
        <div className={meta.touched && meta.error ? `${classes.input} ${classes.errorField} ${classes.error_text}` : `${classes.input}`}>
          <DatePicker
            placeholderText={props.placeholder}
            selected={(field.value && new Date(field.value)) || null}
            showYearPicker={props.showYearPicker}
            dateFormat={props.dateFormat}
            disabled={props.disabled}
            onChange={(val) => {
              if (val) {
                setFieldValue(field.name, `${val.toLocaleDateString()}`);
              }
            }}
            customInput={<DateInput inputref={inputref} />}
          />
        </div>

        {props.details && (
          <small className={classes.small} style={{ marginTop: "8px" }}>
            {props.details}
          </small>
        )}
      </div>
    </div>
  );
};

const DateInput = forwardRef((props: any, ref: any) => (
  <input disabled={props.disabled} {...props} ref={ref} style={props.disabled ? { background: `#F2F2F2 url(${calendar}) no-repeat right 10px center`, paddingRight: "45px" } : { background: `url(${calendar}) no-repeat right 10px center`, paddingRight: "45px" }} />
));

interface IDateInputWithCalendarToggleProps {
  name: string;
  disabled?: boolean;
  format?: string;
  toggleIcon: () => void;
  onBlur?: () => void;
  onChange: (val: Dayjs) => void;
  value?: string;
  inputClassName?: string;
}

export const DateInputWithCalendarToggle = ({inputClassName = 'w-4/5', ...props}: IDateInputWithCalendarToggleProps) => {
  var customParseFormat = require('dayjs/plugin/customParseFormat')
  dayjs.extend(customParseFormat)
  const { isMobile } = useWindowSize();
  const [date, setDate] = useState(props.value || '');
  const ref = useRef<HTMLInputElement>(null);
  const handleChangeDate = () => {
    const inputDate = dayjs(date);
    if (inputDate.isValid()) {
      props.onChange(inputDate);
    } else {
      props.onChange(dayjs(null)); 
    }
  };


  useEffect(() => {
    if (props.value !== date) {
      setDate(props.value || '');
    }
  }, [props.value]);
  
  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={ref}
        value={date}
        name={props.name}
        className={`text-current font-normal text-base pt-3 pb-3 pr-2 pl-4 rounded my-2 outline-none ${inputClassName}`}
        placeholder="Select date"
        pattern='MM/DD/YYYY'
        onBlur={handleChangeDate}
        onChange={(event) => setDate(event.target.value)}
      />
      <Button
        style={{
          background: 'transparent',
          position: 'absolute',
          right: '10px',
          top: '50%',
          padding: 0,
          transform: 'translate(0,-50%)'
        }}
        disabled={props.disabled}
        className=""
        onClick={props.toggleIcon}
        img={<img src={calendar} alt="calendar-icon" />}
        text={""}
      />
    </div>
  )

}

DateInputWithCalendarToggle.defaultProps = {
  disabled: false,
  format: 'MM/DD/YYYY'
}

interface DatePickerFieldInterface {
  name: string;
  labelName: string;
  placeholder?: string;
  showYearPicker: boolean;
  disabled?: boolean;
  dateFormat: string;
  details?: string;
  tooltip?: {
    placement?: "bottom" | "top" | "right" | "left";
    isDisabled?: 0 | 1;
    space?: number;
    text: string;
  };
}
DatePickerFields.defaultProps = {
  placeholder: "Select a date",
  dateFormat: "MM/dd/yyyy",
  showYearPicker: false,
};

export default DatePickerFields;
